import React, { useEffect, useEffectEvent, useState } from 'react';

import { useEditor } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

const MIN_MESSAGES = 5;
const MAX_MESSAGES = 20;
const DEBUG_ANNOUNCEMENTS = false;

interface MessageEntry {
  id: number;
  message: string;
}

let nextMessageId = 0;

const useStyles = makeStyles()({
  /** Hide the announcer except to screen readers. */
  announcer: {
    borderWidth: 0,
    clip: 'rect(0, 0, 0, 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
});

/**
 * Announce messages to the screen reader when the editor.announce method is called while this
 * component is mounted.
 */
export function AnnounceEditor() {
  const editor = useEditor();
  const [messages, setMessages] = useState<MessageEntry[]>([]);
  const { classes } = useStyles();

  const announce = useEffectEvent((message: string) =>
    setMessages(prevMessages => {
      const processedMessage = processMessage(message);
      const messagesToKeep =
        prevMessages.length >= MAX_MESSAGES - 1
          ? prevMessages.slice(1 - MIN_MESSAGES)
          : prevMessages;

      if (DEBUG_ANNOUNCEMENTS) console.log(processedMessage);
      return [...messagesToKeep, { id: nextMessageId++, message: processedMessage }];
    })
  );

  useEffect(() => {
    editor.announce = announce;
    return () => {
      delete editor.announce;
    };
  }, [editor]);

  return (
    <div aria-atomic={false} aria-live="polite" className={classes.announcer} role="region">
      {messages.map(({ id, message }) => (
        <div key={id}>{message}</div>
      ))}
    </div>
  );
}

function processMessage(message: string): string {
  if (message === ' ') return 'space';
  return message;
}
